import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { faDumbbell } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import { t } from "i18next";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DndContext, DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { Exercise } from "@/app/exercises/exercises";
import { usePersonalInformationQuery } from "@/app/personal-informations/personal-informations.api";
import { Routine } from "@/app/routines/routines";
import { useRoutinesQuery } from "@/app/routines/routines.api";
import { searchableOptions, TRAINING_DAYS } from "@/app/training/training";
import { useUpdateTrainingMutation } from "@/app/training/training.api";
import { TrainingCategory } from "@/app/training/training-category";
import { TrainingDay } from "@/app/training/training-day";
import { useUserQuery } from "@/app/users/users.api";
import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";
import { SideBarTraining } from "@/shared/components/organisms/SideBarTraining";
import RoutineTable, {
  RoutineEventHandler,
  RoutineEventPayload,
} from "@/shared/components/organisms/TableListExercisesWeek";
import TableListSeriesMuscles from "@/shared/components/organisms/TableListSeriesMuscles";
import RoutineTabs, {
  MoveTabEventHandler,
} from "@/shared/components/organisms/TabsEdit";
import {
  exerciseSearchInitialValues,
  trainingInitialValues,
} from "@/shared/schemas/training.schema";
import { TopTrainingEditor } from "./TopTrainingEditor";
import Modals from "./Modals";
import {
  useCreateNewRoutineMutation,
  useGetAlgorithmRoutinesQuery,
  useGetProgressionRoutineQuery,
  useGetRoutinesByIdQuery,
  useGetUserPlaningForPositionQuery,
  useSaveRoutineMutation,
} from "../InformationUserPages/api/user-active-plan.api";
import transformData from "./MapingRoutine";
import { useExercisesQuery } from "@/app/exercises/exercises.api";
import { transformTrainingPlan } from "@/shared/components/utils/SaveConverter";
import { toasts } from "@/shared/utils/toasts";
import { PlaneadoModal } from "./PlaneadoModal";

type changeExerciseType = {
  exercise: Exercise;
  day: TrainingDay;
  category: TrainingCategory;
  index: number;
};

export const EditTrainingPages: FC<any> = () => {
  const [searchParams] = useSearchParams();
  const [updateTraining, updateTrainingResult] = useUpdateTrainingMutation();
  const [page, setPage] = useState(1);
  const [currentDay, setCurrentDay] = useState(TRAINING_DAYS[0]);
  const [routinePayload, setRoutinePayload] = useState<
    RoutineEventPayload | undefined
  >();
  const [currentRoutine, setCurrentRoutine] = useState<Routine | undefined>();
  const [WorkingSets, setWorkingSets] = useState<any>();
  const [isDragging, setIsDragging] = useState(false);

  const navigate = useNavigate();

  const id = useMemo(() => searchParams.get("id"), [searchParams]);
  const userId =
    useMemo(() => searchParams.get("user_id"), [searchParams]) ||
    useMemo(() => searchParams.get("userId"), [searchParams]);
  const position = useMemo(() => searchParams.get("position"), [searchParams]);
  const name = useMemo(() => searchParams.get("name"), [searchParams]);

  const planet_posible = useMemo(
    () => searchParams.get("planet_posible"),
    [searchParams]
  );

  const is_wizard = useMemo(
    () => searchParams.get("is_wizard"),
    [searchParams]
  );
  const edit: boolean | any = useMemo(
    () => searchParams.get("edit"),
    [searchParams]
  );
  const active = useMemo(() => searchParams.get("active"), [searchParams]);

  const progress = useMemo(() => searchParams.get("progress"), [searchParams]);

  const period = useMemo(() => searchParams.get("period"), [searchParams]);

  const algorithm: boolean | any = useMemo(
    () => searchParams.get("algorithm"),
    [searchParams]
  );

  const plan = useMemo(() => searchParams.get("plan"), [searchParams]);

  const trainingProgressId = useMemo(
    () => searchParams.get("training_progress_id"),
    [searchParams]
  );
  const newPlan = useMemo(() => searchParams.get("new"), [searchParams]);
  const age = useMemo(() => searchParams.get("age"), [searchParams]);
  const genre = useMemo(() => searchParams.get("genre"), [searchParams]);
  const load = useMemo(() => searchParams.get("load"), [searchParams]);

  const userQuery = useUserQuery({ id: userId });
  const [routines, setRoutines] = useState<any>({});
  const routinesQuery = useRoutinesQuery(
    {
      trainingPlanId: id ?? trainingProgressId,
      name: name,
      position: position,
    },
    {
      skip: !id && !trainingProgressId,
    }
  );

  const exerciseSearchFormik = useFormik({
    initialValues: exerciseSearchInitialValues,
    onSubmit: () => {},
  });

  const [exercisesQuery, setExercisesQuery] = useState<any>([]);
  const [hasMore, setHasMore] = useState(true);

  const { data, isLoading }: any = useExercisesQuery({
    search: exerciseSearchFormik.values.search,
    muscle: exerciseSearchFormik.values.muscle,
    type: exerciseSearchFormik.values.type,
    equipment: exerciseSearchFormik.values.equipment,
    branchId: userQuery.data?.branchId,
    page,
  });

  const clearTermsExercises = () => {
    exerciseSearchFormik.resetForm();
    setExercisesQuery([]);
    setPage(1);
  };

  useEffect(() => {
    if (data) {
      setExercisesQuery((prevExercises: any) => [
        ...prevExercises,
        ...data?.data,
      ]);
      setHasMore(data.length > 0);
    }
  }, [data]);

  const resetExercises = () => {
    setExercisesQuery([]);
    setPage(1);
  };

  useEffect(() => {
    if (data?.data) {
      setExercisesQuery([]);

      setExercisesQuery((prevExercises: any) =>
        page === 1 ? data.data : [...prevExercises, ...data.data]
      );
    }
  }, [data, page]);

  const personalInformationQuery = usePersonalInformationQuery(
    {
      userId: userId,
      status: 1,
    },
    { skip: !userId }
  );

  const [addTrainingPlan] = useSaveRoutineMutation();

  const [saveLoader, setSaveLoader] = useState(false);
  const [openModalAcceptSearchTraining, setOpenModalAcceptSearchTraining] =
    useState(false);

  const [currentPerid, setCurrentPerid] = useState(0);

  useEffect(() => {
    if (currentPerid !== 2) {
      setCurrentPerid(period ? 1 : 0);
    }
  }, [period]);

  const [validationErrors, setValidationErrors] = useState<{
    day: TrainingDay | null;
    category: TrainingCategory | null;
    index: number | null;
    field: string | null;
  }>({
    day: null,
    category: null,
    index: null,
    field: null,
  });

  const [openModalPlanAssignment, setOpenModalPlanAssignment] = useState(false);
  const handleOpenModalPlanAssignment = () =>
    setOpenModalPlanAssignment(!openModalPlanAssignment);
  const [PLANEADO, setPLANEADO] = useState(0);
  const onHandleSelectPlanAssignment = (value: 0 | 1) => {
    setOpenModalPlanAssignment(false);
    if (value === 0) {
      setPLANEADO(0);
    } else {
      setPLANEADO(1);
    }
  };

  /*/   const validateTrainingPlan = () => {
    for (const day of TRAINING_DAYS) {
      const sections = trainingFormik.values.training[day];
      for (const category of [
        "warm",
        "main",
        "stretching",
      ] as TrainingCategory[]) {
        const routines = sections[category].routine;
        for (let i = 0; i < routines.length; i++) {
          const routine = routines[i];
          //@ts-ignore
          if (!routine.serie || routine?.serie?.trim?.() === "") {
            setValidationErrors({ day, category, index: i, field: "serie" });
            return false;
          }
          if (
            !routine.repetitions ||
            //@ts-ignore
            routine.repetitions === "" ||
            routine.repetitions < 1
          ) {
            setValidationErrors({
              day,
              category,
              index: i,
              field: "repetitions",
            });
            return false;
          }
          if (
            category !== "stretching" &&
            (!routine.intensity ||
              //@ts-ignore
              routine.intensity === "" ||
              routine.intensity < 1)
          ) {
            setValidationErrors({
              day,
              category,
              index: i,
              field: "intensity",
            });
            return false;
          }
          if (
            !routine.superSerie &&
            //@ts-ignore
            (!routine?.break || routine?.break === "" || routine?.break < 1)
          ) {
            setValidationErrors({ day, category, index: i, field: "break" });
            return false;
          }
        }
      }
    }
    setValidationErrors({
      day: null,
      category: null,
      index: null,
      field: null,
    });
    return true;
  }; */

  const validateTrainingPlan = () => {
    // Validación existente de campos obligatorios
    for (const day of TRAINING_DAYS) {
      const sections = trainingFormik.values.training[day];
      for (const category of [
        "warm",
        "main",
        "stretching",
      ] as TrainingCategory[]) {
        const routines = sections[category].routine;
        for (let i = 0; i < routines.length; i++) {
          const routine = routines[i];
          //@ts-ignore
          if (!routine.serie || routine?.serie?.trim?.() === "") {
            setValidationErrors({ day, category, index: i, field: "serie" });
            return false;
          }
          if (
            !routine.repetitions ||
            //@ts-ignore
            routine.repetitions === "" ||
            routine.repetitions < 1
          ) {
            setValidationErrors({
              day,
              category,
              index: i,
              field: "repetitions",
            });
            return false;
          }
          if (
            category !== "stretching" &&
            (!routine.intensity ||
              //@ts-ignore
              routine.intensity === "" ||
              routine.intensity < 1)
          ) {
            setValidationErrors({
              day,
              category,
              index: i,
              field: "intensity",
            });
            return false;
          }
          if (
            !routine.superSerie &&
            //@ts-ignore
            (!routine?.break || routine?.break === "" || routine?.break < 1)
          ) {
            setValidationErrors({ day, category, index: i, field: "break" });
            return false;
          }
        }
      }
    }

    // Nueva validación para la regla de categorías
    for (const day of TRAINING_DAYS) {
      const sections = trainingFormik.values.training[day];
      const warmHasExercises = sections.warm.routine.length > 0;
      const mainHasExercises = sections.main.routine.length > 0;
      const stretchingHasExercises = sections.stretching.routine.length > 0;

      // Contar cuántas categorías tienen ejercicios
      const categoriesWithExercises = [
        warmHasExercises,
        mainHasExercises,
        stretchingHasExercises,
      ].filter(Boolean).length;

      // Si no hay ejercicios en ninguna categoría (0), está bien
      // Si todas las categorías tienen ejercicios (3), está bien
      // Si hay ejercicios en 1 o 2 categorías, es un error
      if (categoriesWithExercises > 0 && categoriesWithExercises < 3) {
        setValidationErrors({
          day,
          category: null,
          index: null,
          field: "incomplete_categories",
        });
        return false;
      }
    }

    setValidationErrors({
      day: null,
      category: null,
      index: null,
      field: null,
    });
    return true;
  };

  const handleChangePeriod = (newPeriod: string) => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set("period", newPeriod);
    navigate(`?${currentParams.toString()}`);
    //reload
    window.location.reload();
  };
  /* 
  const handleOpenModalAcceptSearchTraining = () => {
    if (!validateTrainingPlan()) {
      //@ts-ignore
      toasts.danger("Por favor complete todos los campos");
      return;
    }

    setOpenModalAcceptSearchTraining(!openModalAcceptSearchTraining);
  }; */

  const handleOpenModalAcceptSearchTraining = () => {
    if (!validateTrainingPlan()) {
      if (
        validationErrors.field === "incomplete_categories" &&
        validationErrors.day
      ) {
        const day = validationErrors.day;
        const sections = trainingFormik.values.training[day];

        // Traducción de días a español
        const dayTranslations: { [key: string]: string } = {
          monday: "Lunes",
          tuesday: "Martes",
          wednesday: "Miércoles",
          thursday: "Jueves",
          friday: "Viernes",
          saturday: "Sábado",
          sunday: "Domingo",
        };
        const dayName = dayTranslations[day] || day;

        // Traducción de categorías a español
        const categoryTranslations: { [key: string]: string } = {
          warm: "Calentamiento",
          main: "Principal",
          stretching: "Estiramiento",
        };

        const warmHasExercises = sections.warm.routine.length > 0;
        const mainHasExercises = sections.main.routine.length > 0;
        const stretchingHasExercises = sections.stretching.routine.length > 0;

        // Contar categorías con ejercicios
        const categoriesWithExercises = [
          warmHasExercises,
          mainHasExercises,
          stretchingHasExercises,
        ].filter(Boolean).length;

        if (categoriesWithExercises > 0 && categoriesWithExercises < 3) {
          // Identificar categorías con ejercicios
          const filledCategories = [];
          if (warmHasExercises)
            filledCategories.push(categoryTranslations["warm"]);
          if (mainHasExercises)
            filledCategories.push(categoryTranslations["main"]);
          if (stretchingHasExercises)
            filledCategories.push(categoryTranslations["stretching"]);

          // Mensaje en el formato exacto solicitado
          const message = `completa la tabla del dia ${dayName}`;
          toasts.danger(message);
        }
      } else {
        toasts.danger("Por favor completa todos los campos obligatorios");
      }
      return;
    }

    if (planet_posible === "1") {
      if(period && period === "1"){
        setOpenModalAcceptSearchTraining(true);
      }else{
            setOpenModalPlanAssignment(true);
      }
  
    } else {
      setOpenModalAcceptSearchTraining(!openModalAcceptSearchTraining);
    }
  };

  const [createNewRoutine, createNewRoutineResult] =
    useCreateNewRoutineMutation();

  const addleSave = async () => {
    //@ts-ignore
    const trainingPlan = transformTrainingPlan({
      ...trainingFormik.values,
      name: trainingFormik?.values?.name,
      is_period: period ? Number(period) : 0,
      is_wizard: is_wizard ? Number(is_wizard) : 0,
      nowPlan: /* edit ? 0 : */ 1,
      skip_to_planned: PLANEADO,
      new_weeks_amount: trainingFormik?.values?.weeks,
      user_id: Number(userId),
      week: trainingFormik?.values?.weeks,
      searchable: trainingFormik?.values?.searchable,
    });

    try {
      if (newPlan) {
        setSaveLoader(true);

        const data = await createNewRoutine({
          data: trainingPlan,
        });
        navigate(`/users/${userId}`);
      } else {
        setSaveLoader(true);

        const result = await addTrainingPlan({
          data: trainingPlan,
          id: routines?.data?.pCustomize?.id,
        });
        if (period === "2") {
          navigate(`/users/${userId}`);
        }
        if (period === "1") {
          handleChangePeriod("2");
          /* @ts-ignore */
          toasts.success("Plan guardado correctamente");
          setOpenModalAcceptSearchTraining(false);
          /* @ts-ignore */
          toasts.success("actualmente es editando el plan con regla");
        } else {
          navigate(`/users/${userId}`);
        }
      }
    } catch (error) {
      console.error("Error al guardar el plan:", error);
    } finally {
      setSaveLoader(false);
    }
  };

  const trainingFormik = useFormik({
    initialValues: trainingInitialValues,
    onSubmit: async () => await addleSave(),
  });

  const addAllExercisesToForm = (exerciseData: any, trainingFormik: any) => {
    const days = Object.keys(exerciseData.days);

    days.forEach((day) => {
      const categories = Object.keys(exerciseData.days[day]);

      categories.forEach((category) => {
        const exercises = exerciseData.days[day][category].exercises;

        exercises.forEach((exercise: any, index: number) => {
          trainingFormik.setFieldValue(
            `training.${day}.${category}.routine[${index}]`,
            {
              day,
              category,
              exercise: exercise?.exercise,
              break: exercise?.break,
              serie: exercise?.serie,
              superSerie: exercise?.super_serie,
              repetitions: exercise?.rep?.replace(";", ","),
              intensity: exercise?.intensity,
              um: exercise?.um,
              muscle: exercise?.muscle,
              evolutions: exercise?.evolutions,
              commentary_custom: exercise?.commentary_custom,
            }
          );
        });
      });
    });
  };

  function musclesByDay() {
    const valuesNotPermited = [
      "Calentamiento",
      "Cardio",
      "Traccion",
      "Empuje",
      "Trapecios",
      "Acductor",
    ];
    const valuesPriorityMuscle = [
      "Cuádriceps",
      "Deltoides",
      "Espalda",
      "Pectorales",
      "Bíceps",
      "Gemelos",
      "Glúteos",
      "Isquiotibiales",
      "Tríceps",
      "Core",
      "Abductor",
      "Antebrazo",
    ];

    return TRAINING_DAYS.reduce((workingSets, trainingDay) => {
      const day = trainingFormik.values.training[trainingDay];
      const routine = day ? day.main.routine : [];
      const workingRoutines = routine.filter(
        (routine) => Number(routine?.intensity) >= 70
      );

      const arrMuscle = [];
      workingRoutines.forEach((routine: any) => {
        const muscle = routine?.exercise?.first_main_muscle;
        if (
          muscle &&
          !valuesNotPermited
            .map((v) => v.toLowerCase())
            .includes(muscle.toLowerCase())
        ) {
          arrMuscle.push(muscle);
        }
      });

      // Ordenar según valuesPriorityMuscle
      //@ts-ignore
      const prioritizedMuscles = [];
      for (let j = 0; j < valuesPriorityMuscle.length; j++) {
        if (
          //@ts-ignore
          arrMuscle.includes(valuesPriorityMuscle[j]) &&
          //@ts-ignore
          !prioritizedMuscles.includes(valuesPriorityMuscle[j])
        ) {
          prioritizedMuscles.push(valuesPriorityMuscle[j]);
          if (prioritizedMuscles.length >= 4) break;
        }
      }

      return {
        ...workingSets,
        [trainingDay]: {
          series: workingRoutines.reduce((total, routine) => {
            if (routine?.superSerie) return total;
            return total + Number(routine.serie);
          }, 0),
          muscles: prioritizedMuscles,
        },
      };
    }, {});
  }

  useEffect(() => {
    if (routines?.data) {
      const routine = transformData(routines?.data);

      addAllExercisesToForm(routine, trainingFormik);
    }
  }, [routines?.data]);

  useEffect(() => {
    const workingSets = musclesByDay();
    setWorkingSets(workingSets);
  }, [trainingFormik.values]);

  const routine = useGetRoutinesByIdQuery({ id });

  /*   const progressRoutine = useGetProgressionRoutineQuery({ userId: userId }); pruma */

  const algorithmResult = useGetAlgorithmRoutinesQuery({
    table_name: plan,
    plan_id: position,
  });

  const positionData = useGetUserPlaningForPositionQuery({ position });

  useEffect(() => {
    if (active) {
      setRoutines(positionData);
    } else if (algorithm) {
      setRoutines(algorithmResult);
    } else {
      setRoutines(routine);
    }
  }, [
    id,
    plan,
    position,
    routine?.data,
    algorithmResult?.data,
    positionData?.data,
  ]);

  const workingSets = useMemo(() => {
    return TRAINING_DAYS.reduce((workingSets, trainingDay) => {
      const day = trainingFormik.values.training[trainingDay];
      const routine = day ? day.main.routine : [];
      const workingRoutines = routine.filter(
        (routine) => Number(routine?.intensity) >= 70
      );

      const uniqueMuscles = new Set();
      workingRoutines.forEach((routine: any) => {
        const muscle = routine?.exercise?.first_main_muscle; // Usar solo first_main_muscle

        if (
          (muscle && muscle.toLowerCase() !== "cardio") ||
          "Trapecios" ||
          "Acductor"
        ) {
          uniqueMuscles.add(muscle);
        }
      });

      return {
        ...workingSets,
        [trainingDay]: {
          series: workingRoutines.reduce(
            (total, routine) => total + Number(routine.serie),
            0
          ),
          muscles: Array.from(uniqueMuscles),
        },
      };
    }, {});
  }, [trainingFormik.values, routines, validateTrainingPlan]);

  const handleAddRoutine = (exercise: Exercise, category: TrainingCategory) => {
    const index =
      trainingFormik?.values?.training[currentDay]?.[category]?.routine
        ?.length || 0;
    const newRoutine = {
      day: currentDay,
      category,
      exercise,
      break: null,
      serie: null,
      superSerie: false,
      repetitions: null,
      intensity: null,
    };
    trainingFormik.setFieldValue(
      `training.${currentDay}.${category}.routine[${index}]`,
      newRoutine
    );
  };

  const handleRemoveRoutine: RoutineEventHandler = ({
    day,
    category,
    index,
  }) => {
    trainingFormik.setFieldValue(
      `training.${day}.${category}.routine`,
      trainingFormik?.values?.training[day]?.[category]?.routine?.filter(
        (_, _index) => _index != index
      )
    );
  };

  const handleMoveUpRoutine: RoutineEventHandler = ({
    day,
    category,
    index,
  }) => {
    const sourceRoutine =
      trainingFormik.values.training[day][category].routine[index];

    const targetRoutine =
      trainingFormik.values.training[day][category].routine[index - 1];

    trainingFormik.setFieldValue(
      `training.${day}.${category}.routine[${index - 1}]`,
      sourceRoutine
    );
    trainingFormik.setFieldValue(
      `training.${day}.${category}.routine[${index}]`,
      targetRoutine
    );
  };

  const handleMoveDownRoutine: RoutineEventHandler = ({
    day,
    category,
    index,
  }) => {
    const sourceRoutine =
      trainingFormik.values.training[day][category].routine[index];

    const targetRoutine =
      trainingFormik.values.training[day][category].routine[index + 1];

    trainingFormik.setFieldValue(
      `training.${day}.${category}.routine[${index + 1}]`,
      sourceRoutine
    );
    trainingFormik.setFieldValue(
      `training.${day}.${category}.routine[${index}]`,
      targetRoutine
    );
  };

  const changeExercise = ({
    exercise,
    index,
    day,
    category,
  }: changeExerciseType) => {
    const selectedRoutine: any =
      //@ts-ignore
      trainingFormik?.values?.training[day]?.[category]?.routine[index];

    if (selectedRoutine) {
      selectedRoutine.exercise = exercise?.exercise;
      trainingFormik?.setFieldValue(
        `training.${day}.${category}.routine[${index}].exercise`,
        selectedRoutine.exercise
      );
    }
  };

  const handleMoveTabRight: MoveTabEventHandler = (trainingDay) => {
    const targetTrainingDay =
      TRAINING_DAYS[TRAINING_DAYS.indexOf(trainingDay) + 1];

    const sourceTraining = trainingFormik.values.training[trainingDay];
    const targetTraining = trainingFormik.values.training[targetTrainingDay];

    trainingFormik.setFieldValue(
      `training[${targetTrainingDay}]`,
      sourceTraining
    );
    trainingFormik.setFieldValue(`training[${trainingDay}]`, targetTraining);

    setCurrentDay(targetTrainingDay);
  };

  const [hayCambiosSinGuardar, setHayCambiosSinGuardar] = useState(false);

  // Handler para el evento beforeunload
  const manejarSalida = (e) => {
    if (hayCambiosSinGuardar) {
      const mensaje = "¿Descartar los cambios y salir?";
      e.preventDefault();
      e.returnValue = mensaje; // Necesario para algunos navegadores
      return mensaje;
    }
  };

  // Efecto para agregar y limpiar el event listener
  useEffect(() => {
    // Agregar el listener cuando el componente se monta
    window.addEventListener("beforeunload", manejarSalida);

    // Cleanup: remover el listener cuando el componente se desmonta
    return () => {
      window.removeEventListener("beforeunload", manejarSalida);
    };
  }, [hayCambiosSinGuardar]); // Se actualiza cuando cambia el estado

  const handleMoveTabLeft: MoveTabEventHandler = (trainingDay) => {
    const targetTrainingDay =
      TRAINING_DAYS[TRAINING_DAYS.indexOf(trainingDay) - 1];

    const sourceTraining = trainingFormik.values.training[trainingDay];
    const targetTraining = trainingFormik.values.training[targetTrainingDay];

    trainingFormik.setFieldValue(
      `training[${targetTrainingDay}]`,
      sourceTraining
    );
    trainingFormik.setFieldValue(`training[${trainingDay}]`, targetTraining);

    setCurrentDay(targetTrainingDay);
  };

  const onDayChange = (day: TrainingDay) => {
    setCurrentDay(day);
  };

  const [openNewDivForExercises, setOpenNewDivForExercises] = useState(false);
  const handleOpenNewDivForExercises = () =>
    setOpenNewDivForExercises(!openNewDivForExercises);

  const [openModalInfoExercise, setOpenModalInfoExercise] = useState(false);
  const handleOpenModalInfoExercise = () =>
    setOpenModalInfoExercise(!openModalInfoExercise);

  const [openEquipmentExercise, setOpenEquipmentExercise] = useState("others");

  const addComment = ({ message, index, category, day }: any) => {
    const selectedRoutine: any =
      //@ts-ignore
      trainingFormik?.values?.training[day]?.[category]?.routine[index];
    if (selectedRoutine) {
      selectedRoutine.commentary_custom = message;
      trainingFormik?.setFieldValue(
        `training.${day}.${category}.routine[${index}].commentary_custom`,
        selectedRoutine.commentary_custom
      );
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    const draggedExercise = exercisesQuery.find(
      (ex: Exercise) =>
        //@ts-ignore
        (ex.id || `${exercisesQuery.indexOf(ex)}`) === event.active.id
    );
    if (draggedExercise) {
      event.active.data.current = {
        exercise: draggedExercise,
        type: "EXERCISE",
      };
    }
  };

  const [weeks, setWeeks] = useState("");
  const [objective, setObjective] = useState("");

  //names
  const [traningName, setTraningName] = useState(
    routines?.data?.pCustomize?.plan
  );

  useEffect(() => {
    setWeeks(newPlan ? 4 : routines?.data?.pCustomize?.week);
    setObjective(
      newPlan ? "hypertrophy" : routines?.data?.pCustomize?.objective
    );
    setTraningName(newPlan ? "nuevo plan" : routines?.data?.pCustomize?.plan);
    //form
    trainingFormik.setFieldValue(
      "weeks",
      newPlan ? 4 : routines?.data?.pCustomize?.week
    );
    trainingFormik.setFieldValue(
      "objective",
      newPlan ? "hypertrophy" : routines?.data?.pCustomize?.objective
    );
    trainingFormik.setFieldValue(
      "name",
      newPlan ? "nuevo plan" : routines?.data?.pCustomize?.plan
    );
  }, [routines?.data]);

  const handleAddEvolution = ({ evolution, index, category, day }: any) => {
    const selectedRoutine: any =
      //@ts-ignore
      trainingFormik?.values?.training[day]?.[category]?.routine[index];
    if (selectedRoutine) {
      selectedRoutine.evolutions = evolution;
      trainingFormik?.setFieldValue(
        `training.${day}.${category}.routine[${index}].evolutions`,
        selectedRoutine.evolutions
      );
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setIsDragging(false);

    if (!active || !over || active.id === over.id) return;

    // Case 1: Reordering within RoutineTable
    const categories = ["warm", "main", "stretching"];
    for (const category of categories) {
      const routine =
        trainingFormik.values.training[currentDay][category].routine;
      const activeIndex = routine.findIndex(
        (r: any) =>
          (r.id || `${currentDay}-${category}-${routine.indexOf(r)}`) ===
          active.id
      );
      const overIndex = routine.findIndex(
        (r: any) =>
          (r.id || `${currentDay}-${category}-${routine.indexOf(r)}`) ===
          over.id
      );

      if (activeIndex !== -1 && overIndex !== -1) {
        const newRoutine = arrayMove(routine, activeIndex, overIndex);
        trainingFormik.setFieldValue(
          `training.${currentDay}.${category}.routine`,
          newRoutine
        );
        return;
      }
    }

    // Case 2: Dropping a new exercise from the sidebar
    if (active.data.current?.type === "EXERCISE" && over.id) {
      const droppedExercise = active.data.current.exercise;
      const [droppedDay, droppedCategory] = over.id.toString().split("-"); // e.g., "monday-warm-droppable"

      if (droppedDay && droppedCategory) {
        handleAddRoutine(droppedExercise, droppedCategory as TrainingCategory);
      }
    }
  };
  const loaderPlan = newPlan
    ? false
    : algorithmResult?.isLoading || positionData.isLoading;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    console.log("newPage", newPage);
  };

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 sm:grid-cols-12">
        <div className="md:hidden col-span-12 px-4 h-full">
          <div
            onClick={handleOpenNewDivForExercises}
            className="block w-full rounded-lg mt-5 py-5 border-2 border-dashed border-gray-300 text-center"
          >
            <FontAwesomeIcon
              icon={faDumbbell}
              className="mx-auto h-12 w-12 text-gray-400"
            />
            <span className="mt-2 block text-sm font-semibold text-gray-900">
              {t("user.views.add_excersise")}
            </span>
          </div>
        </div>
        <div className="col-span-12 md:col-span-11 flex flex-col px-4 h-full">
          <TopTrainingEditor
            week={weeks}
            onWeekChange={(e: any) => {
              setWeeks(e.target.value);
              trainingFormik.setFieldValue("weeks", e.target.value);
            }}
            objective={objective}
            newPlan={newPlan}
            objectiveChange={(e) => {
              trainingFormik.setFieldValue("objective", e?.target.value);
              setObjective(e?.target.value);
            }}
            onTrainingNameChange={(e) => {
              setTraningName(e.target.value);
              trainingFormik.setFieldValue("name", e.target.value);
            }}
            name={traningName}
            /* ==== */

            age={age}
            genre={genre}
            trainingFormik={trainingFormik}
            userQuery={userQuery}
            personalInformationQuery={personalInformationQuery}
            userId={userId}
          />

          <TableListSeriesMuscles
            workingSets={WorkingSets}
            isLoading={loaderPlan}
            title={t("user.views.titleTableMusclesSeries")}
          />

          <RoutineTabs
            days={TRAINING_DAYS}
            currentDay={currentDay}
            onDayChange={onDayChange}
            onMoveLeft={handleMoveTabLeft}
            onMoveRight={handleMoveTabRight}
          />

          <div>
            {TRAINING_DAYS.map((day) => (
              <div key={day} className={`${day == currentDay ? "" : "hidden"}`}>
                <RoutineTable
                  spanRight={
                    userQuery?.data?.user?.branch?.measurement_unit as any
                  }
                  onChange={trainingFormik.handleChange}
                  onBlur={trainingFormik.handleBlur}
                  validationErrors={validationErrors}
                  onMoveUpRoutine={handleMoveUpRoutine}
                  onMoveDownRoutine={handleMoveDownRoutine}
                  onRemoveRoutine={handleRemoveRoutine}
                  onAddComment={addComment}
                  onChangeRoutineExercise={(payload: any) =>
                    changeExercise(payload)
                  }
                  title={t("trainingEditor.table.warm")}
                  day={day}
                  category={TrainingCategory.Warm}
                  isLoading={loaderPlan}
                  section={trainingFormik.values.training[day]?.warm}
                  onClickViewExercise={setCurrentRoutine}
                  onDropExercise={(exercise) =>
                    handleAddRoutine(exercise, TrainingCategory.Warm)
                  }
                  exerciseLevanted={isDragging}
                />
                <div className="border-t border-gray-200 mt-10"></div>
                <RoutineTable
                  exerciseLevanted={isDragging}
                  spanRight={userQuery?.data?.user?.branch?.measurement_unit}
                  handleAddEvolution={handleAddEvolution}
                  validationErrors={validationErrors}
                  onAddComment={addComment}
                  onChange={trainingFormik.handleChange}
                  onBlur={trainingFormik.handleBlur}
                  onMoveUpRoutine={handleMoveUpRoutine}
                  onMoveDownRoutine={handleMoveDownRoutine}
                  onRemoveRoutine={handleRemoveRoutine}
                  onChangeRoutineExercise={(payload: any) =>
                    changeExercise(payload)
                  }
                  title={t("trainingEditor.table.main")}
                  day={day}
                  category={TrainingCategory.Main}
                  isLoading={loaderPlan}
                  rir={true}
                  section={trainingFormik.values.training[day]?.main}
                  onClickViewExercise={setCurrentRoutine}
                  onDropExercise={(exercise) =>
                    handleAddRoutine(exercise, TrainingCategory.Main)
                  }
                />
                <div className="border-t border-gray-200 mt-10"></div>
                <RoutineTable
                  spanRight={userQuery?.data?.user?.branch?.measurement_unit}
                  handleAddEvolution={handleAddEvolution}
                  validationErrors={validationErrors}
                  onAddComment={addComment}
                  onChange={trainingFormik.handleChange}
                  onBlur={trainingFormik.handleBlur}
                  onMoveUpRoutine={handleMoveUpRoutine}
                  onMoveDownRoutine={handleMoveDownRoutine}
                  onRemoveRoutine={handleRemoveRoutine}
                  onChangeRoutineExercise={(payload: any) =>
                    changeExercise(payload)
                  }
                  title={t("trainingEditor.table.stretching")}
                  day={day}
                  category={TrainingCategory.Stretching}
                  isLoading={loaderPlan}
                  section={trainingFormik.values.training[day]?.stretching}
                  onClickViewExercise={setCurrentRoutine}
                  onDropExercise={(exercise) =>
                    handleAddRoutine(exercise, TrainingCategory.Stretching)
                  }
                  exerciseLevanted={isDragging}
                />
              </div>
            ))}
          </div>
        </div>
        {/* Capa Derecha */}
        <div className="col-span-1 hidden md:block">
          {/*           <SideBarTraining
            currentPage={page}
            onPageChange={handlePageChange}
            isLoading={isLoading}
            exercises={exercisesQuery ?? []}
            onAddRoutine={handleAddRoutine}
            formik={exerciseSearchFormik}
            //@ts-ignore
            isDragging={isDragging}
          /> */}

          <SideBarTraining
            currentPage={page}
            onPageChange={handlePageChange}
            isLoading={isLoading}
            exercises={exercisesQuery ?? []}
            onAddRoutine={handleAddRoutine}
            formik={exerciseSearchFormik}
            resetExercises={resetExercises}
            //@ts-ignore
            isDragging={isDragging}
          />
        </div>
        <div className="col-span-1 ml-5 pb-10 mt-5">
          <ButtonPrimary
            size="extraLarge"
            text={t("general.save")}
            onClick={handleOpenModalAcceptSearchTraining}
          />
        </div>

        <PlaneadoModal
          onClickCreate={() => setOpenModalAcceptSearchTraining(true)}
          openModalPlanAssignment={openModalPlanAssignment}
          handleOpenModalPlanAssignment={handleOpenModalPlanAssignment}
          onHandleSelect={onHandleSelectPlanAssignment}
        />

        <Modals
          saveLoader={saveLoader}
          openModalAcceptSearchTraining={openModalAcceptSearchTraining}
          handleOpenModalAcceptSearchTraining={() => {
            console.log("handleOpenModalAcceptSearchTraining");
            setOpenModalAcceptSearchTraining(false);
            setOpenModalPlanAssignment(false);
          }}
          trainingFormik={trainingFormik}
          searchableOptions={searchableOptions}
          openModalInfoExercise={openModalInfoExercise}
          handleOpenModalInfoExercise={handleOpenModalInfoExercise}
          openEquipmentExercise={openEquipmentExercise}
        />
      </div>
    </DndContext>
  );
};
